import ApiService from '@/services/api';
import Bid from '@/entities/Bid';
import { EntityBuilder } from '@decahedron/entity';
import User from '@/entities/User';
import Car from '@/entities/Car';
import Formatter from '@/support/formatter';
import Variables from '@/config/variables';

let instance: BidService = null;

export default class BidService {
    protected ownBids: Bid[] = null;

    protected formatter = new Formatter();

    protected variables = new Variables();

    apiService: ApiService = null;

    bidsList: Bid[] = null;

    get own(): Bid[] {
        if (!this.ownBids) {
            this.getOwn()
                .then((bids) => {
                    this.ownBids = bids;
                });
        }

        return this.ownBids || [];
    }

    get ownLatest(): Bid[] {
        return this.own
            .sort((a, b) => (a.amount > b.amount ? -1 : 1))
            .reduce((reduced: Bid[], bid) => (reduced.some((reducedBid) => reducedBid.car.id === bid.car.id) ? reduced : [...reduced, bid]), []);
    }

    constructor() {
        if (!instance) {
            this.apiService = new ApiService();
            instance = this;
        }

        return instance;
    }

    get list() {
        if (!this.bidsList) {
            this.getList().then((bids) => {
                this.bidsList = bids;
            });
        }

        return this.bidsList || [];
    }

    calculate(amount: number, car: Car) {
        const vat = car.vatDeductible ? Math.ceil(amount * (this.variables.vat / 100)) : 0;
        const total = amount + vat + this.variables.fee;

        return {
            amount: {
                value: amount,
                formatted: this.formatter.currency(amount),
            },
            vat: {
                value: vat,
                formatted: this.formatter.currency(vat),
            },
            fee: {
                value: this.variables.fee,
                formatted: this.variables.feeString,
            },
            total: {
                value: total,
                formatted: this.formatter.currency(total),
            },
        };
    }

    getList() {
        return this.apiService.get('bids')
            .then((response) => EntityBuilder.buildMany<Bid>(Bid, response.data));
    }

    isOwnBid(carId: string, amount: number): boolean {
        return this.own
            .some((bid) => bid.car.id === carId && bid.amount === amount);
    }

    findOwnBid(carId: string): Bid {
        return this.ownLatest
            .find((bid) => bid.car.id === carId);
    }

    getOwn() {
        return this.apiService.get('bids/own')
            .then((response) => EntityBuilder.buildMany<Bid>(Bid, response.data));
    }

    addBid(carId: string, amount: number) {
        const data = new FormData();
        data.append('car_id', carId);
        data.append('amount', amount.toString());
        return this.apiService.post('bids', data)
            .then((response) => {
                const bid = EntityBuilder.buildOne(Bid, response.data);

                this.ownBids.push(bid);

                return bid;
            });
    }
}
